import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Copy, Trash2, Edit2, ExternalLink, CopyPlus, Check, Globe, GlobeLock, Heart, Layers } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import { getPaletteById, updatePaletteName, updatePalettePublicName, updatePaletteMoods } from '../config/savedPalettes';
import { updatePaletteHideLikes } from '../services/galleryService';
import MoodPicker from './MoodPicker';
import { parseMoods, serializeMoods } from '../utils/moods';
import { getMarkerCode } from '../utils/color';
import { useConfirmModal } from './ConfirmModal';

export default function PaletteDetailModal({ isOpen, paletteId, preloadedPalette, preloadedMarkers, onClose, onDelete, onDuplicate, onNameUpdate, onPublicNameUpdate, onMoodsUpdate, onTogglePublic, isToggling, isTrusted, heartCount }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { ohuhuMode } = useAppContext();
  const { showConfirm, ConfirmModalComponent } = useConfirmModal();
  const [palette, setPalette] = useState(preloadedPalette || null);
  const [markers, setMarkers] = useState(preloadedMarkers || []);
  const [isLoading, setIsLoading] = useState(!preloadedPalette);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(preloadedPalette?.name || '');
  const [isEditingPublicName, setIsEditingPublicName] = useState(false);
  const [editedPublicName, setEditedPublicName] = useState(preloadedPalette?.publicName || '');
  const [isCopied, setIsCopied] = useState(false);
  const [moodNames, setMoodNames] = useState(parseMoods(preloadedPalette?.moods));
  const [hideLikes, setHideLikes] = useState(!!preloadedPalette?.hideLikes);
  const moodSaveTimer = useRef(null);

  const handleToggleHideLikes = async () => {
    const newVal = !hideLikes;
    setHideLikes(newVal);
    await updatePaletteHideLikes(user.uid, paletteId || preloadedPalette?.id, newVal);
  };

  const handleMoodsChange = (newMoodNames) => {
    setMoodNames(newMoodNames);
    const moodsString = serializeMoods(newMoodNames);
    clearTimeout(moodSaveTimer.current);
    moodSaveTimer.current = setTimeout(async () => {
      await updatePaletteMoods(user.uid, paletteId || preloadedPalette?.id, moodsString || null);
      if (onMoodsUpdate) onMoodsUpdate(moodsString || null);
    }, 800);
  };

  useEffect(() => {
    if (preloadedPalette && preloadedMarkers) {
      setPalette(preloadedPalette);
      setMarkers(preloadedMarkers);
      setEditedName(preloadedPalette.name);
      setEditedPublicName(preloadedPalette.publicName || '');
      setHideLikes(!!preloadedPalette.hideLikes);
      setIsLoading(false);
      return;
    }

    if (isOpen && paletteId && user) {
      loadPalette();
    }
  }, [isOpen, paletteId, user, preloadedPalette, preloadedMarkers]);

  const loadPalette = async () => {
    setIsLoading(true);
    try {
      const result = await getPaletteById(user.uid, paletteId);
      if (result.success) {
        setPalette(result.palette);
        setMarkers(result.markers);
        setEditedName(result.palette.name);
        setEditedPublicName(result.palette.publicName || '');
      } else {
        await showConfirm({
          title: t('savedPalettes.alerts.error'),
          message: t('savedPalettes.alerts.loadFailed') + ': ' + result.error,
          confirmText: 'OK',
          variant: 'warning',
          showCancel: false
        });
        onClose();
      }
    } catch (error) {
      await showConfirm({
        title: t('savedPalettes.alerts.error'),
        message: t('savedPalettes.alerts.loadFailed') + ': ' + error.message,
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (!palette) return;
    navigator.clipboard.writeText(palette.code).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(async (err) => {
      console.error('Failed to copy:', err);
      await showConfirm({
        title: t('savedPalettes.alerts.error'),
        message: t('savedPalettes.alerts.copyFailed'),
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
    });
  };

  const handleLoadToCreate = () => {
    if (!palette) return;
    onClose();
    navigate(`/?code=${encodeURIComponent(palette.code)}`);
  };

  const handleFindShades = () => {
    if (!palette || !markers || markers.length === 0) return;
    onClose();
    navigate('/shades', {
      state: {
        palette: markers,
        paletteName: palette.name,
        paletteCode: palette.code,
      },
    });
  };

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      await showConfirm({
        title: t('savedPalettes.alerts.error'),
        message: t('savedPalettes.errors.nameRequired'),
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
      return;
    }

    try {
      const result = await updatePaletteName(user.uid, paletteId, editedName);
      if (result.success) {
        const newName = editedName.trim();
        setPalette({ ...palette, name: newName });
        setIsEditing(false);
        if (onNameUpdate) onNameUpdate(newName);
      } else {
        await showConfirm({
          title: t('savedPalettes.alerts.error'),
          message: t('savedPalettes.alerts.updateFailed') + ': ' + result.error,
          confirmText: 'OK',
          variant: 'warning',
          showCancel: false
        });
      }
    } catch (error) {
      await showConfirm({
        title: t('savedPalettes.alerts.error'),
        message: t('savedPalettes.alerts.updateFailed') + ': ' + error.message,
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
    }
  };

  const handleSavePublicName = async () => {
    try {
      const result = await updatePalettePublicName(user.uid, paletteId || preloadedPalette?.id, editedPublicName);
      if (result.success) {
        const trimmed = editedPublicName.trim();
        setPalette({ ...palette, publicName: trimmed || undefined });
        setEditedPublicName(trimmed);
        setIsEditingPublicName(false);
        if (onPublicNameUpdate) onPublicNameUpdate(trimmed || null);
      }
    } catch (error) {
      console.error('Failed to update public name:', error);
    }
  };

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => { if (e.key === 'Escape' && !isEditing) onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, isEditing, onClose]);

  if (!isOpen) return null;

  const allColors = markers.map(m => m.hex);

  // Resolve marker code taking ohuhuMode into account
  const getCode = (marker) => getMarkerCode(marker, ohuhuMode);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-50 backdrop-blur-sm bg-black/30 flex items-center justify-center p-4 animate-fadeOnly"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        aria-modal="true"
        role="dialog"
      >
        <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-100 flex flex-col max-h-[90vh] animate-slideUp">

          {/* Top color strip */}
          <div className="flex h-28 rounded-t-3xl overflow-hidden flex-shrink-0">
            {allColors.length > 0
              ? allColors.map((hex, i) => (
                  <div key={i} className="flex-1" style={{ backgroundColor: hex }} />
                ))
              : <div className="flex-1 bg-slate-100" />
            }
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-5">

            {/* Title row */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-base"
                      maxLength={50}
                      autoFocus
                    />
                    <button
                      onClick={handleSaveName}
                      className="p-1.5 text-emerald-600 hover:text-emerald-700 transition-colors"
                      title={t('savedPalettes.buttons.save')}
                    >
                      <Check size={18} />
                    </button>
                    <button
                      onClick={() => { setIsEditing(false); setEditedName(palette.name); }}
                      className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 min-w-0">
                    <h2 className="text-lg font-bold text-slate-900 leading-tight truncate">
                      {palette?.name || t('savedPalettes.detailModal.title')}
                    </h2>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="p-1 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
                      title={t('savedPalettes.buttons.editName')}
                    >
                      <Edit2 size={14} />
                    </button>
                  </div>
                )}
                {palette && !isEditing && (
                  <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
                    {palette.isPublic && heartCount !== undefined && (
                      <>
                        <Heart size={13} className="fill-red-400 text-red-400" />
                        <span>{heartCount}</span>
                        <span className="text-slate-300">&middot;</span>
                      </>
                    )}
                    {new Date(palette.createdAt).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </p>
                )}
                {/* Public name + hide likes — only for trusted publishers */}
                {isTrusted && !isEditing && (
                  <div className="mt-2 space-y-2">
                    {/* Hide likes toggle — only relevant when palette is public */}
                    {palette?.isPublic && (
                      <button
                        onClick={handleToggleHideLikes}
                        className="flex items-center gap-2 group"
                      >
                        <div className={`w-7 h-4 rounded-full transition-colors flex-shrink-0 flex items-center px-0.5 ${hideLikes ? 'bg-slate-400' : 'bg-emerald-400'}`}>
                          <div className={`w-3 h-3 rounded-full bg-white shadow transition-transform ${hideLikes ? 'translate-x-0' : 'translate-x-3'}`} />
                        </div>
                        <span className="text-xs text-slate-400 group-hover:text-slate-600 transition-colors">
                          {hideLikes ? 'Like count hidden on gallery' : 'Like count visible on gallery'}
                        </span>
                      </button>
                    )}
                    {isEditingPublicName ? (
                      <div className="flex items-center gap-2">
                        <Globe size={13} className="text-slate-400 flex-shrink-0" />
                        <input
                          type="text"
                          value={editedPublicName}
                          onChange={(e) => setEditedPublicName(e.target.value)}
                          placeholder="Public gallery name (optional)"
                          className="flex-1 px-2.5 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          maxLength={50}
                          autoFocus
                        />
                        <button onClick={handleSavePublicName} className="p-1 text-emerald-600 hover:text-emerald-700 transition-colors" title="Save">
                          <Check size={15} />
                        </button>
                        <button onClick={() => { setIsEditingPublicName(false); setEditedPublicName(palette.publicName || ''); }} className="p-1 text-slate-400 hover:text-slate-600 transition-colors">
                          <X size={15} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setIsEditingPublicName(true)}
                        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors group"
                      >
                        <Globe size={12} />
                        {palette.publicName
                          ? <span className="text-slate-500">{palette.publicName}</span>
                          : <span className="italic">Set this palette name for the gallery...</span>
                        }
                        <Edit2 size={11} className="text-slate-400" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {!isEditing && (
                <button
                  onClick={onClose}
                  className="p-2 rounded-xl hover:bg-slate-100 transition-colors flex-shrink-0"
                >
                  <X size={18} className="text-slate-500" />
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="flex justify-center py-10">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-emerald-500 rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Swatch grid — code inside square, brand below */}
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
                  {markers.map((marker, i) => {
                    const hex = marker.hex || '#E5E7EB';
                    const cleanHex = hex.replace('#', '');
                    const r = parseInt(cleanHex.substr(0, 2), 16);
                    const g = parseInt(cleanHex.substr(2, 2), 16);
                    const b = parseInt(cleanHex.substr(4, 2), 16);
                    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                    const textColor = luminance > 0.5 ? '#1e293b' : '#ffffff';
                    const rawCode = getCode(marker);
                    const codeSegments = rawCode.split('/').map(s => s.trim()).filter(Boolean);
                    return (
                    <div key={i} className="flex flex-col">
                      <div
                        className="w-full aspect-square rounded-xl border-4 border-white shadow-inner relative"
                        style={{ backgroundColor: hex }}
                      >
                        <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-1">
                          {codeSegments.map((seg, idx) => (
                            <span
                              key={idx}
                              className="px-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.3em]"
                              style={{ color: textColor }}
                            >
                              {seg}
                            </span>
                          ))}
                        </div>
                      </div>
                    <p className="text-[8px] font-semibold uppercase text-slate-500 truncate mt-1.5 px-2">
                        {marker.brand}
                      </p>
                    </div>
                    );
                  })}
                </div>

                {/* Code box */}
                {palette?.code && (
                  <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2 mb-4">
                    <p className="text-xs font-mono text-slate-600 flex-1 break-all leading-relaxed">
                      {palette.code}
                    </p>
                    <button
                      onClick={handleCopyCode}
                      className="p-2 rounded-lg hover:bg-slate-200 transition-colors flex-shrink-0"
                      title={t('savedPalettes.buttons.copyCode')}
                    >
                      {isCopied
                        ? <Check size={15} className="text-green-600" />
                        : <Copy size={15} className="text-slate-500" />
                      }
                    </button>
                  </div>
                )}

                {/* Mood picker */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Moods</p>
                  <MoodPicker selected={moodNames} onChange={handleMoodsChange} />
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-wrap items-center gap-2 p-5 pt-0 flex-shrink-0">
            <button
              onClick={handleLoadToCreate}
              className="flex items-center gap-1.5 px-4 py-2 mt-4 rounded-xl text-xs font-medium bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-all"
            >
              <ExternalLink size={15} />
              {t('savedPalettes.buttons.loadToCreate')}
            </button>
            <button
              onClick={handleFindShades}
              className="flex items-center gap-1.5 px-4 py-2 mt-4 rounded-xl text-xs font-medium bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-100 transition-all"
            >
              <Layers size={15} />
              Find shades
            </button>

            <div className="ml-auto flex items-center gap-1">
              {onTogglePublic && (
                <button
                  onClick={onTogglePublic}
                  disabled={isToggling}
                  className={`p-2 rounded-xl transition-colors disabled:opacity-40 ${
                    palette?.isPublic
                      ? 'text-emerald-500 hover:bg-emerald-50'
                      : 'text-slate-400 hover:bg-slate-100'
                  }`}
                  title={palette?.isPublic ? 'Make private' : 'Make public'}
                >
                  {palette?.isPublic ? <Globe size={18} /> : <GlobeLock size={18} />}
                </button>
              )}
              <button
                onClick={onDuplicate}
                className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                title={t('savedPalettes.buttons.duplicate')}
              >
                <CopyPlus size={18} />
              </button>
              <button
                onClick={onDelete}
                className="p-2 rounded-xl text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                title={t('savedPalettes.buttons.delete')}
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
      {ConfirmModalComponent}
    </>,
    document.body
  );
}
